import type { ReactNode } from "react";

import { useBreakdown } from "../../api/hooks.ts";
import { formatPercent, formatR, formatSignedPips, toneOf } from "../../domain/format.ts";
import { Icon } from "../../ui/Icon.tsx";
import { StatusBlock } from "../../ui/StatusBlock.tsx";

/**
 * Los pips POR INSTRUMENTO, que es donde sumar pips si mide algo (ADR 0007):
 * dentro de un mismo simbolo, un pip es un pip. Es el desglose de SPEC 11 por
 * `instrument`, mirado desde la columna de pips y con el R al lado.
 *
 * El `note` del desglose se pinta si llega, aunque por instrumento no deberia
 * mezclar nada: la regla es que lo que el servidor dice, se ensena.
 */
export function InstrumentPips(props: {
  from?: string | undefined;
  className?: string | undefined;
}): ReactNode {
  const breakdown = useBreakdown("instrument", props.from);
  const rows = breakdown.data?.rows ?? [];
  const note = breakdown.data?.note ?? null;

  return (
    <section className={`panel ${props.className ?? ""}`}>
      <div className="panel-h">
        <div>
          <h3>Pips por instrumento</h3>
          <p>Donde sumar pips si mide algo</p>
        </div>
        <Icon name="layers" />
      </div>
      <div className="panel-b">
        <StatusBlock
          loading={breakdown.isPending}
          error={breakdown.error}
          empty={rows.length === 0}
          emptyIcon="chart"
          emptyTitle="Sin operaciones cerradas."
          emptyText="Cierra la primera y aqui aparece su simbolo."
          onRetry={() => void breakdown.refetch()}
        >
          {note !== null && (
            <p className="infobox">
              <b>Nota del servidor.</b> {note}
            </p>
          )}
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">Instrumento</th>
                  <th scope="col">Ops.</th>
                  <th scope="col">Pips</th>
                  <th scope="col">R</th>
                  <th scope="col">Acierto</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key ?? row.label}>
                    <th scope="row" style={{ textAlign: "left", fontWeight: 600 }}>
                      <span className="sym">{row.label}</span>
                    </th>
                    <td>{row.trades}</td>
                    <td className={toneOf(row.totalPips)}>{formatSignedPips(row.totalPips)}</td>
                    <td className={toneOf(row.totalR)}>{formatR(row.totalR)}</td>
                    <td>{formatPercent(row.winRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </StatusBlock>
      </div>
    </section>
  );
}
